<script setup lang="ts">
    import { useDisplay } from 'vuetify';
    import { useI18n } from 'vue-i18n';
    import { VForm } from 'vuetify/components';
    import { AppError } from '@/errors/appError';
    import { dateFormatInUserTimeZone } from '@/helpers';

    const { t } = useI18n();

    const formRef = ref<VForm>();
    const isDialogOpen = defineModel<boolean>();

    const props = withDefaults(
        defineProps<{
            width?: number | string;
            title?: string;
            height?: string;
            lastEditedDate?: string;
            isShowActionButtons?: boolean;
            onSubmit: () => Promise<boolean> | boolean;
        }>(),
        { isShowActionButtons: true }
    );

    const emit = defineEmits(['saved', 'cancelled', 'opened']);

    const loading = ref(false);

    const close = () => {
        isDialogOpen.value = false;
    };

    watchEffect(() => {
        if (isDialogOpen.value) {
            emit('opened');
        }
    });

    const display = useDisplay();

    const cancel = () => {
        //do not close if loading
        if (loading.value) {
            return;
        }
        emit('cancelled');
        close();
    };

    async function confirmAction() {
        if (formRef.value === undefined) {
            throw new AppError('Form is undefined on confirmAction');
        }

        const result = await formRef.value.validate();

        if (!result.valid) {
            document.querySelector('.v-input--error:first-of-type')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            return;
        }

        loading.value = true;
        try {
            const result = await props.onSubmit();
            if (result) {
                emit('saved');
                close();
            }
        } catch (error) {
            console.error('Save error:', error);
        } finally {
            loading.value = false;
        }
    }

    const width = computed(() => {
        if (typeof props.width !== 'undefined') {
            return props.width;
        }

        switch (display.name.value) {
            case 'xs':
                return 220;
            case 'sm':
                return 400;
            case 'md':
                return 500;
            case 'lg':
                return 600;
            case 'xl':
                return 800;
            default:
                return 500;
        }
    });
    onMounted(() => {});
</script>

<template>
    <v-dialog
        scrollable
        persistent
        :model-value="isDialogOpen"
        @keydown.esc="cancel"
        @keydown.enter="confirmAction"
        :width="width"
        :fullscreen="display.xs.value"
        content-class="top-dialog"
        :no-click-animation="true"
        :height="height"
    >
        <v-card class="pa-3 dialog">
            <slot name="title" :cancel="cancel">
                <v-card-title class="py-1 pl-3 pr-1 d-flex justify-space-between align-center" style="height: 48px">
                    <span class="text-h5">{{ title }}</span>
                    <v-spacer></v-spacer>
                    <span v-if="lastEditedDate" class="font13 greyText">
                        <v-icon :data-tooltip="t('action.updated')" size="18" style="padding-bottom: 2px" :icon="mdiCalendarClockOutline" />
                        {{ dateFormatInUserTimeZone(lastEditedDate, 'dd MMM yyyy HH:mm') }}
                    </span>
                    <v-btn tag="a" @click="cancel" :icon="mdiClose" variant="text" size="36" density="default"></v-btn>
                </v-card-title>
            </slot>
            <slot name="content">
                <v-card-text class="pb-0 pt-0 px-1">
                    <div>
                        <v-form ref="formRef" @submit.prevent>
                            <slot></slot>
                        </v-form>
                    </div>
                </v-card-text>
            </slot>

            <v-divider v-if="isShowActionButtons"></v-divider>

            <v-card-actions class="pb-0 pl-0 pr-2" v-if="isShowActionButtons" style="min-height: 44px">
                <slot name="beforeActions"></slot>
                <v-spacer></v-spacer>
                <v-btn tag="a" color="grey" variant="text" @click="cancel()" density="default">{{ t('action.cancel') }}</v-btn>
                <v-btn tag="a" :loading="loading" :disabled="loading" color="tbGreen" style="color: white" elevation="0" @click="confirmAction()" variant="flat" density="default">
                    {{ t('action.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
    .v-theme--dark .v-card.dialog {
        border: 1px solid #404148;
    }
</style>
